import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, Search } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import loggingService from '../../services/loggingService';
import CommunityOnboarding from './CommunityOnboarding/CommunityOnboarding';
import SiteDetails from './SiteDetails';
import SiteForm from './SiteForm';
import SiteList from './SiteList';
import './SiteManagement.css';
import SiteMap from './SiteMap';
import { getAllSites, getSiteDetails, parseGeofenceDetails, updateGeofence } from './siteApi';

const DEFAULT_MAP_CENTER = [20.5937, 78.9629];

const getSiteMapCenter = site => {
  if (!site) {
    return DEFAULT_MAP_CENTER;
  }

  const geofenceDetails = parseGeofenceDetails(site.geofence_details);

  if (geofenceDetails?.type === 'Circle' && geofenceDetails.center) {
    const centerValue = geofenceDetails.center;
    const lat = Array.isArray(centerValue)
      ? centerValue[0]
      : centerValue.latitude ?? centerValue.lat;
    const lng = Array.isArray(centerValue)
      ? centerValue[1]
      : centerValue.longitude ?? centerValue.lng;

    if (typeof lat === 'number' && typeof lng === 'number') {
      return [lat, lng];
    }
  }

  if (
    geofenceDetails?.type === 'Polygon' &&
    Array.isArray(geofenceDetails.coordinates) &&
    geofenceDetails.coordinates.length > 0
  ) {
    let latSum = 0;
    let lngSum = 0;
    let validCount = 0;

    geofenceDetails.coordinates.forEach(coord => {
      const lat = Array.isArray(coord)
        ? coord[0]
        : coord.latitude ?? coord.lat;
      const lng = Array.isArray(coord)
        ? coord[1]
        : coord.longitude ?? coord.lng;

      if (typeof lat === 'number' && typeof lng === 'number') {
        latSum += lat;
        lngSum += lng;
        validCount += 1;
      }
    });

    if (validCount > 0) {
      return [latSum / validCount, lngSum / validCount];
    }
  }

  if (
    typeof site.latitude === 'number' &&
    typeof site.longitude === 'number'
  ) {
    return [site.latitude, site.longitude];
  }

  return DEFAULT_MAP_CENTER;
};

const SiteManagement = () => {
  // Log screen view
  useScreenLogging('SiteManagement', {
    component: 'SiteManagement',
  });

  const navigate = useNavigate();
  const location = useLocation();
  const { siteId: communityRouteSiteId } = useParams();
  const [currentView, setCurrentView] = useState('list');
  const [selectedSite, setSelectedSite] = useState(null);
  const [editingSite, setEditingSite] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [siteDetailsRefreshKey, setSiteDetailsRefreshKey] = useState(0);
  const [geofenceEditMode, setGeofenceEditMode] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [geofenceHistory, setGeofenceHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [allSites, setAllSites] = useState([]); // All sites with geofence details for map display
  const [loadingSites, setLoadingSites] = useState(true);
  const [communitySiteName, setCommunitySiteName] = useState('');
  const siteMapRef = useRef(null); // Ref to SiteMap component for zooming

  // Check if we're on the create route
  const isCreateRoute = location.pathname.includes('/create');
  const isCommunityRoute = Boolean(communityRouteSiteId) && location.pathname.includes('/community');

  const handleSiteSelect = site => {
    setSelectedSite(site);
    // In 2-column layout, we stay in 'list' view but show details in right panel
    if (currentView === 'list') {
      // Stay in list view, just update selected site to show details
    } else {
      setCurrentView('details');
    }
  };

  // Handle site marker click from map
  const handleSiteMarkerClick = (site) => {
    // Find the full site data from allSites
    const fullSite = allSites.find(s => s.id === site.id || s.site_id === site.id);
    if (fullSite && fullSite.originalSite) {
      // Use the original site data to set selectedSite
      setSelectedSite(fullSite.originalSite);
      // Zoom to the site's geofence
      if (siteMapRef.current) {
        siteMapRef.current.zoomToSiteGeofence(fullSite);
      }
    }
  };

  const handleCreateNew = () => {
    setEditingSite(null);
    setSelectedSite(null);
    // Navigate to create route for full-screen form
    navigate('/onboarding-site-onboarding/create');
  };

  const handleEditSite = site => {
    setEditingSite(site);
    // Keep the site selected so we can show the form in the right panel
    if (!selectedSite || selectedSite.site_id !== site.site_id) {
      setSelectedSite(site);
    }
    // Don't change view - stay in list view to show form in right panel
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedSite(null);
    setEditingSite(null);
    // Navigate back to main site management page
    navigate('/onboarding-site-onboarding');
  };

  const handleBackToDetails = () => {
    setCurrentView('details');
    setEditingSite(null);
    // Reset geofence edit mode when going back
    setGeofenceEditMode(false);
    setGeofenceHistory([]);
    setHistoryIndex(-1);
  };

  const handleFormSuccess = async () => {
    if (isCreateRoute) {
      handleBackToList();
      return;
    }

    const siteId = selectedSite?.site_id;
    setEditingSite(null);

    if (!siteId) {
      return;
    }

    try {
      const refreshedSite = await getSiteDetails(siteId);
      setSelectedSite(refreshedSite);
      setSiteDetailsRefreshKey(prev => prev + 1);
      setAllSites(prevSites =>
        prevSites.map(site =>
          site.site_id === siteId
            ? {
                ...site,
                site_name: refreshedSite.site_name,
                address: refreshedSite.address,
                geofence_details: refreshedSite.geofence_details,
                originalSite: refreshedSite,
              }
            : site
        )
      );
    } catch (error) {
      console.error('Failed to refresh site after update:', error);
      setSelectedSite({ ...selectedSite });
    }
  };

  const handleSiteDelete = siteId => {
    setCurrentView('list');
    setSelectedSite(null);
    setEditingSite(null);
    if (isCommunityRoute && communityRouteSiteId === siteId) {
      navigate('/onboarding-site-onboarding');
    }
  };

  const handleCommunityOnboarding = site => {
    setEditingSite(null);
    setGeofenceEditMode(false);
    navigate(`/onboarding-site-onboarding/${site.site_id}/community`, {
      state: { siteName: site.site_name },
    });
  };

  const handleCommunityOnboardingBack = () => {
    const returnSiteId = communityRouteSiteId;
    navigate('/onboarding-site-onboarding', {
      state: returnSiteId ? { selectedSiteId: returnSiteId } : undefined,
    });
  };

  const handleCommunityOnboardingComplete = () => {
    alert('Community setup saved successfully.');
    handleCommunityOnboardingBack();
  };

  const handleGeofenceEdit = () => {
    if (selectedSite) {
      setGeofenceEditMode(true);
      // Initialize history with current geofence
      const currentGeofence = selectedSite.geofence_details;
      setGeofenceHistory([currentGeofence]);
      setHistoryIndex(0);
    }
  };

  const handleGeofenceChange = newGeofence => {
    if (geofenceEditMode) {
      // Add to history
      const newHistory = geofenceHistory.slice(0, historyIndex + 1);
      newHistory.push(newGeofence);
      setGeofenceHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);

      // Update selected site
      setSelectedSite(prev => ({
        ...prev,
        geofence_details: newGeofence,
      }));

      // Also update editingSite to keep form in sync
      if (editingSite && selectedSite && editingSite.site_id === selectedSite.site_id) {
        setEditingSite(prev => ({
          ...prev,
          geofence_details: newGeofence,
        }));
      }
    }
  };

  const handleRadiusChange = newRadius => {
    // Update selectedSite's radius when changed in the form
    if (selectedSite && selectedSite.geofence_details) {
      const geofenceDetails = 
        typeof selectedSite.geofence_details === 'string'
          ? JSON.parse(selectedSite.geofence_details)
          : selectedSite.geofence_details;

      if (geofenceDetails.type === 'Circle') {
        const updatedGeofence = {
          ...geofenceDetails,
          radius: newRadius,
        };

        setSelectedSite(prev => ({
          ...prev,
          geofence_details: updatedGeofence,
        }));

        // Also update editingSite to keep them in sync
        if (editingSite && editingSite.site_id === selectedSite.site_id) {
          setEditingSite(prev => ({
            ...prev,
            geofence_details: updatedGeofence,
          }));
        }
      }
    }
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setSelectedSite(prev => ({
        ...prev,
        geofence_details: geofenceHistory[newIndex],
      }));
    }
  };

  const handleRedo = () => {
    if (historyIndex < geofenceHistory.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setSelectedSite(prev => ({
        ...prev,
        geofence_details: geofenceHistory[newIndex],
      }));
    }
  };

  const handleGeofenceSave = async () => {
    try {
      if (selectedSite && selectedSite.site_id) {
        // Transform geofence data to API format
        let geofenceData = { ...selectedSite.geofence_details };
        
        // Transform circular geofence: convert center from [lat, lng] to { latitude, longitude }
        if (geofenceData && (geofenceData.type === 'Circle' || geofenceData.type === 'Circle')) {
          // Ensure type is lowercase as per API format
          geofenceData.type = 'Circle';
          
          // Convert center from array to object format if needed
          if (Array.isArray(geofenceData.center)) {
            geofenceData.center = {
              latitude: geofenceData.center[0],
              longitude: geofenceData.center[1],
            };
          } else if (geofenceData.center && typeof geofenceData.center === 'object') {
            // Already in object format, but ensure it has latitude/longitude keys
            if (!geofenceData.center.latitude && geofenceData.center.lat) {
              geofenceData.center = {
                latitude: geofenceData.center.lat,
                longitude: geofenceData.center.lng,
              };
            }
          }
        }
        
        await updateGeofence(
          selectedSite.site_id,
          { geofence_details: geofenceData }
        );
        console.log('Geofence updated successfully');
        
        // Log API event for updating geofence
        try {
          await loggingService.logInfo(
            'site_management_update_geofence',
            'Updated site geofence',
            {
              endpoint: '/site/geofence/update',
              method: 'PUT',
              siteId: selectedSite.site_id,
              geofenceType: geofenceData?.type || 'unknown',
            }
          );
        } catch (logError) {
          console.warn('[SiteManagement] Failed to log event:', logError);
        }
        // Update editingSite to sync with the updated geofence
        if (editingSite && editingSite.site_id === selectedSite.site_id) {
          setEditingSite(prev => ({
            ...prev,
            geofence_details: geofenceData,
          }));
        }
        // Show success message or handle success
      }
    } catch (error) {
      console.error('Error updating geofence:', error);
      // Show error message to user
      alert('Failed to update geofence. Please try again.');
      return; // Don't exit edit mode if there was an error
    }

    // Save the geofence changes
    setGeofenceEditMode(false);
    setGeofenceHistory([]);
    setHistoryIndex(-1);
  };

  const handleGeofenceCancel = () => {
    // Revert to original geofence
    if (geofenceHistory.length > 0) {
      setSelectedSite(prev => ({
        ...prev,
        geofence_details: geofenceHistory[0],
      }));
    }
    setGeofenceEditMode(false);
    setGeofenceHistory([]);
    setHistoryIndex(-1);
  };

  // Fetch all sites on component mount
  useEffect(() => {
    const fetchAllSites = async () => {
      try {
        setLoadingSites(true);
        const sitesResponse = await getAllSites();
        const sitesArray = Array.isArray(sitesResponse) ? sitesResponse : [];
        
        // Log API event for fetching sites
        try {
          await loggingService.logInfo(
            'site_management_fetch_sites',
            'Fetched all sites',
            {
              endpoint: '/site/getall',
              method: 'GET',
              sitesCount: sitesArray.length,
            }
          );
        } catch (logError) {
          console.warn('[SiteManagement] Failed to log event:', logError);
        }

        // Parse geofence details and extract coordinates for each site (similar to Dashboard)
        const sitesWithGeofences = sitesArray.map(site => {
          let lat = 0;
          let lng = 0;
          let geofenceDetails = null;

          try {
            const parsedDetails = JSON.parse(site.geofence_details);
            geofenceDetails = parsedDetails;

            if (
              parsedDetails.type === 'Polygon' &&
              parsedDetails.coordinates &&
              parsedDetails.coordinates.length > 0
            ) {
              // For polygons, use the first coordinate as the site's primary lat/lng for marker placement
              lat = parsedDetails.coordinates[0].latitude;
              lng = parsedDetails.coordinates[0].longitude;
            } else if (
              parsedDetails.type === 'Circle' &&
              parsedDetails.center
            ) {
              lat = parsedDetails.center.latitude;
              lng = parsedDetails.center.longitude;
            } else {
              console.warn(
                `Malformed or unsupported geofence_details type for site ${site.site_id}:`,
                site.geofence_details
              );
              // Fallback: Use default coordinates for India if geofence is malformed
              lat = 20.5937;
              lng = 78.9629;
            }
          } catch (parseError) {
            console.error(
              `Error parsing geofence_details for site ${site.site_id}:`,
              parseError,
              site.geofence_details
            );
            // Fallback: Use default coordinates for India if parsing fails
            lat = 20.5937; // Default to center of India
            lng = 78.9629;
          }

          return {
            id: site.site_id,
            site_id: site.site_id, // Keep original site_id for compatibility
            site_name: site.site_name, // Keep original site_name
            address: site.address,
            lat: lat,
            lng: lng,
            geofenceDetails: geofenceDetails,
            geofence_details: site.geofence_details, // Keep original geofence_details string
            radius: geofenceDetails?.radius || 500, // Default radius if not specified in geofenceDetails
            // Store original site data for full details
            originalSite: site,
          };
        });

        setAllSites(sitesWithGeofences);
      } catch (error) {
        console.error('Error fetching all sites:', error);
        setAllSites([]);
      } finally {
        setLoadingSites(false);
      }
    };

    fetchAllSites();
  }, []);

  useEffect(() => {
    if (!isCommunityRoute || !communityRouteSiteId) {
      return;
    }

    if (location.state?.siteName) {
      setCommunitySiteName(location.state.siteName);
      return;
    }

    let cancelled = false;
    const loadSiteName = async () => {
      try {
        const site = await getSiteDetails(communityRouteSiteId);
        if (!cancelled && site?.site_name) {
          setCommunitySiteName(site.site_name);
          navigate(
            { pathname: location.pathname, search: location.search },
            {
              replace: true,
              state: { ...location.state, siteName: site.site_name },
            }
          );
        }
      } catch (error) {
        console.error('Error loading community site details:', error);
      }
    };

    loadSiteName();
    return () => {
      cancelled = true;
    };
  }, [
    isCommunityRoute,
    communityRouteSiteId,
    location.pathname,
    location.search,
    location.state,
    navigate,
  ]);

  useEffect(() => {
    const returnSiteId = location.state?.selectedSiteId;
    if (!returnSiteId || isCommunityRoute || isCreateRoute) {
      return;
    }

    let cancelled = false;
    const restoreSelectedSite = async () => {
      try {
        const site = await getSiteDetails(returnSiteId);
        if (!cancelled) {
          setSelectedSite(site);
        }
      } catch (error) {
        console.error('Error restoring selected site:', error);
      }
    };

    restoreSelectedSite();
  }, [location.state?.selectedSiteId, isCommunityRoute, isCreateRoute]);

  const getBreadcrumbPath = () => {
    // Remove the base path since it's already shown in the main navigation
    switch (currentView) {
      case 'details':
        return ['Site Info'];
      case 'form':
        return editingSite ? ['Site Info', 'Edit Site'] : ['Create Site'];
      default:
        if (geofenceEditMode) {
          return ['Site Info', 'Edit Site', 'Edit Polygon Geofencing'];
        }
        return [];
    }
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'list':
        return (
          <SiteList
            onSiteSelect={handleSiteSelect}
            onCreateNew={handleCreateNew}
            onEditSite={handleEditSite}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />
        );

      case 'details':
        return (
          <SiteDetails
            siteId={selectedSite?.site_id}
            refreshKey={siteDetailsRefreshKey}
            onBack={handleBackToList}
            onEdit={handleEditSite}
            onDelete={handleSiteDelete}
            onCommunity={handleCommunityOnboarding}
          />
        );

      case 'form':
        return (
          <SiteForm
            site={editingSite}
            mode={editingSite ? 'edit' : 'create'}
            onBack={editingSite ? handleBackToDetails : handleBackToList}
            onSuccess={handleFormSuccess}
          />
        );

      default:
        return (
          <SiteList
            onSiteSelect={handleSiteSelect}
            onCreateNew={handleCreateNew}
            onEditSite={handleEditSite}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />
        );
    }
  };

  const getViewTitle = () => {
    switch (currentView) {
      case 'list':
        return 'Site Management';
      case 'details':
        return 'Site Details';
      case 'form':
        return editingSite ? 'Edit Site' : 'Create New Site';
      default:
        return 'Site Management';
    }
  };

  if (isCommunityRoute) {
    return (
      <div className="site-management-container community-route">
        <div className="site-management-content community-route">
          <div className="community-onboarding-page">
            <CommunityOnboarding
              siteId={communityRouteSiteId}
              siteName={communitySiteName || location.state?.siteName}
              onBack={handleCommunityOnboardingBack}
              onComplete={handleCommunityOnboardingComplete}
              isSidebar={false}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`site-management-container ${isCreateRoute ? 'create-route' : ''}`}>
      {/* Breadcrumb Navigation - Only show if there are breadcrumbs */}
      {/* {getBreadcrumbPath().length > 0 && (
        <div className="breadcrumb-container">
          <nav className="breadcrumb-nav">
            {getBreadcrumbPath().map((item, index) => (
              <React.Fragment key={index}>
                <span className="breadcrumb-item">{item}</span>
                {index < getBreadcrumbPath().length - 1 && (
                  <span className="breadcrumb-separator">/</span>
                )}
              </React.Fragment>
            ))}
          </nav>
        </div>
      )} */}

      {/* Main Content - 2 Column Layout */}
      <div className={`site-management-content ${isCreateRoute ? 'create-route' : ''}`}>
        {currentView === 'list' && !isCreateRoute ? (
          <div className="two-column-layout">
            {/* Left - Map View */}
            <div className="left-map">
              {/* Geofence Editing Controls */}
              {geofenceEditMode && (
                <div className="geofence-edit-controls">
                  <div className="geofence-edit-header">
                    <div className="geofence-edit-title-group">
                      <button
                        onClick={handleGeofenceCancel}
                        className="back-button"
                        title="Back"
                      >
                        <ArrowLeft className="w-5 h-5" />
                      </button>
                      <h3 className="geofence-edit-title">
                        {selectedSite?.site_name || 'Site Name'}
                      </h3>
                    </div>
                    <div className="geofence-edit-actions">
                      <button
                        onClick={handleUndo}
                        disabled={historyIndex <= 0}
                        className="undo-button"
                        title="Undo"
                      >
                        ↶
                      </button>
                      <button
                        onClick={handleRedo}
                        disabled={historyIndex >= geofenceHistory.length - 1}
                        className="redo-button"
                        title="Redo"
                      >
                        ↷
                      </button>
                      <button
                        onClick={handleGeofenceSave}
                        className="save-geofence-button"
                      >
                        Update site
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <SiteMap
                ref={siteMapRef}
                center={getSiteMapCenter(selectedSite)}
                geofenceType={selectedSite?.geofence_details?.type || 'Polygon'}
                coordinates={selectedSite?.geofence_details?.coordinates || []}
                radius={selectedSite?.geofence_details?.radius || 100}
                onCenterChange={() => {}}
                onGeofenceChange={handleGeofenceChange}
                isMainView={true}
                selectedSite={selectedSite}
                isEditMode={geofenceEditMode}
                isEditSiteMode={editingSite && selectedSite && editingSite.site_id === selectedSite.site_id}
                onEditGeofence={handleGeofenceEdit}
                allSites={allSites} // Pass all sites with geofences for display
                onSiteMarkerClick={handleSiteMarkerClick} // Handle marker clicks
              />
            </div>

            {/* Right Panel - Site List, Site Details, or Edit Form */}
            <div className="right-panel-content">
              {editingSite && selectedSite && editingSite.site_id === selectedSite.site_id ? (
                <SiteForm
                  site={editingSite}
                  mode="edit"
                  onBack={() => {
                    setEditingSite(null);
                    // Reset geofence edit mode when going back
                    setGeofenceEditMode(false);
                    setGeofenceHistory([]);
                    setHistoryIndex(-1);
                    // Stay on the same site to show details
                  }}
                  onSuccess={handleFormSuccess}
                  isSidebar={true}
                  onEditGeofence={handleGeofenceEdit}
                  onRadiusChange={handleRadiusChange}
                />
              ) : selectedSite ? (
                <SiteDetails
                  siteId={selectedSite?.site_id}
                  refreshKey={siteDetailsRefreshKey}
                  onBack={() => {
                    setSelectedSite(null);
                    setEditingSite(null);
                    setIsDetailsOpen(false);
                  }}
                  onEdit={handleEditSite}
                  onDelete={handleSiteDelete}
                  onCommunity={handleCommunityOnboarding}
                  isSidebar={true}
                />
              ) : (
                <div className="right-sidebar">
                  <div className="sidebar-header">
                    <h2 className="sidebar-title">Select site to edit</h2>
                    <button
                      onClick={handleCreateNew}
                      className="create-site-button"
                    >
                      <span className="plus-icon">+</span>
                      Create Site
                    </button>
                  </div>
                  <div className="search-container">
                    <div className="search-input-wrapper">
                      <Search className="search-icon" />
                      <input
                        type="text"
                        placeholder="Search Site"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="search-input"
                      />
                    </div>
                  </div>
                  <div className="sites-list">
                    <SiteList
                      onSiteSelect={handleSiteSelect}
                      onCreateNew={handleCreateNew}
                      onEditSite={handleEditSite}
                      searchQuery={searchQuery}
                      onSearchChange={setSearchQuery}
                      isSidebar={true}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={currentView}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className={isCreateRoute ? "view-container-no-scroll" : "view-container"}
            >
              {isCreateRoute ? (
                <SiteForm
                  site={null}
                  mode="create"
                  onBack={handleBackToList}
                  onSuccess={handleFormSuccess}
                />
              ) : (
                renderCurrentView()
              )}
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default SiteManagement;
